import {
    type ReactNode,
    Component,
    type MutableRefObject,
    type LegacyRef,
    createRef,
    type Ref,
    type RefObject,
} from 'react';

import { graphql, createFragmentContainer } from 'react-relay/legacy.js';

// components
import { SharedRespRelatedItem } from '../../shared/SharedRespRelatedItem/SharedRespRelatedItem.tsx';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider.tsx';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer.tsx';
import { QuickViewProvider } from 'dibs-buyer-product-tile/exports/QuickView.ts';

// helpers
import classnames from 'classnames';
import {
    trackSimilarItemClick,
    trackSimilarItemImpressions,
} from '../tracking/similarItemsTracking.js';
import { trackItemsDisplayed, trackItemClick } from '../../pdp/tracking/similarItemsTracking.js';

import { type FragmentRefs } from 'relay-runtime';
import { type PdpRespSimilarItemsGrid_similarItems$data } from './__generated__/PdpRespSimilarItemsGrid_similarItems.graphql.ts';
import { type PdpRespSimilarItemsGrid_item$data } from './__generated__/PdpRespSimilarItemsGrid_item.graphql.ts';
import { type PdpRespSimilarItemsGrid_user$data } from './__generated__/PdpRespSimilarItemsGrid_user.graphql.ts';
import { type PdpRespSimilarItemsGrid_viewer$data } from './__generated__/PdpRespSimilarItemsGrid_viewer.graphql.ts';
import { type FavoritesLocation } from '../../utils/tracking/shared/favoritesTracking.tsx';
import { type PdpSimilarItemsMergedEntry } from './mergePdpSimilarItemsAndSponsoredItems.ts';

import {
    VisibilityTracker,
    type VisibilityChangeCallback,
} from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

type GridItems = PdpRespSimilarItemsGrid_similarItems$data;

/** Organic connection nodes and sponsored itemRead refs both satisfy this key shape. */
type PdpRespSimilarItemsGridMergedItem = {
    readonly serviceId?: string | null;
    readonly ' $fragmentSpreads': FragmentRefs<
        | 'FavoritesProvider_item'
        | 'PdpRespSimilarItemsGrid_similarItems'
        | 'SharedRespRelatedItem_item'
    >;
};

const DEFAULT_DATA_TN = 'pdp-similar-recommendation-grid';

export type Props = {
    header?: ReactNode;
    subHeader?: ReactNode;
    relayVariables: {
        isClient: boolean;
    };
    isDark?: boolean;
    hideBar?: boolean;
    similarItems: GridItems;
    mergedEntries?: PdpSimilarItemsMergedEntry<PdpRespSimilarItemsGridMergedItem>[];
    currency?: string;
    gridItemWrapperClass?: string;
    gridWrapperClass?: string;
    gridHeaderClass?: string;
    user?: PdpRespSimilarItemsGrid_user$data | null | undefined;
    viewer?: PdpRespSimilarItemsGrid_viewer$data | null | undefined;
    gridLocation: string;
    favoritesLocation: FavoritesLocation;
    fetchFolder?: boolean;
    dataTn?: string;
    scrollToRef?: Ref<HTMLDivElement>;
    gridItemWrapperRef?: MutableRefObject<HTMLDivElement | undefined>;
    item: PdpRespSimilarItemsGrid_item$data;
    onContentVisible?: () => void;
    onComponentDidMount?: () => void;
    isUnavailablePdp?: boolean;
    isSponsored?: boolean;
    onRowImpression?: (args: { index: number }) => void;
    onLastItemVisibilityChange?: (args: VisibilityChangeCallback) => void;
    onItemClick?: (index: number) => void;
    onItemImpression?: (index: number) => void;
    /** Shimmer tiles rendered inside the container so the header survives the loading state. */
    placeholder?: ReactNode;
};

type State = { contentIsVisible: boolean };
export class PdpRespSimilarItemsGridComponent extends Component<Props, State> {
    static defaultProps = {
        isDark: true,
        dataTn: DEFAULT_DATA_TN,
        isSponsored: false,
    };

    constructor(props: Props) {
        super(props);

        this.onClickItem = this.onClickItem.bind(this);
        this.onVisibleItem = this.onVisibleItem.bind(this);
        this.onContentVisible = this.onContentVisible.bind(this);
        this.isSponsoredItem = this.isSponsoredItem.bind(this);
        this.hasFiredTracking = false;

        this.state = {
            contentIsVisible: false,
        };

        this.gridContainerRef = createRef<HTMLDivElement>();
    }

    componentDidMount(): void {
        if (this.props.onComponentDidMount) {
            this.props.onComponentDidMount();
        }
    }

    componentDidUpdate(): void {
        if (
            this.props.similarItems?.[0]?.ecommerceTrackingParams &&
            this.state.contentIsVisible &&
            !this.hasFiredTracking &&
            !this.props.onRowImpression &&
            !this.props.onItemImpression
        ) {
            this.hasFiredTracking = true;
            if (this.props.onContentVisible) {
                this.props.onContentVisible();
            } else {
                trackItemsDisplayed({
                    moduleType: 'PDP_SIMILAR_ITEMS',
                    actionValue: 'bottom fold',
                });
            }
            const items = this.props.similarItems.map((item, index) =>
                this.isSponsoredItem(index) ? { ...item, isSponsored: true } : item
            );
            trackSimilarItemImpressions(items, this.props.gridLocation);
        }
    }

    hasFiredTracking: boolean;
    gridContainerRef: RefObject<HTMLDivElement | null>;
    isSponsoredItem(index: number): boolean {
        return !!this.props.mergedEntries?.[index]?.sponsoredMetadata || !!this.props.isSponsored;
    }
    onContentVisible(): void {
        this.setState({ contentIsVisible: true });
    }

    onClickItem(index: number): void {
        const item = this.props.similarItems[index];
        const isSponsored = !!this.isSponsoredItem(index);
        trackItemClick({ moduleType: 'PDP_SIMILAR_ITEMS', actionValue: 'bottom fold' });
        trackSimilarItemClick(index, { ...item, isSponsored }, this.props.gridLocation);
        this.props.onItemClick?.(index);
    }

    onVisibleItem(index: number): void {
        this.props.onItemImpression?.(index);
    }

    render(): ReactNode {
        const gridItems = this.props?.similarItems || [];
        const gridEntries =
            this.props.mergedEntries ??
            gridItems.map(gridItem => ({
                item: gridItem,
                sponsoredMetadata: this.props.isSponsored ? {} : null,
            }));

        const { placeholder } = this.props;
        if (!gridEntries.length && !placeholder) {
            return null;
        }

        const { contentIsVisible } = this.state;
        const {
            isDark,
            currency = '',
            user = null,
            viewer = null,
            gridWrapperClass,
            gridItemWrapperClass,
            gridHeaderClass,
            favoritesLocation,
            header,
            subHeader,
            dataTn,
            scrollToRef,
            item,
            gridItemWrapperRef,
            hideBar,
            isUnavailablePdp,
            isSponsored,
            onRowImpression,
            onLastItemVisibilityChange,
        } = this.props;
        const { isClient } = this.props.relayVariables;
        const favoritesProviderProps = {
            location: favoritesLocation,
            disable: !isClient || !contentIsVisible,
            userId: user?.serviceId ?? null,
            items: gridItems,
            fetchFolder: this.props.fetchFolder,
        };

        return (
            <PdpRespSimilarItemsContainer
                isUnavailablePdp={isUnavailablePdp}
                isSponsored={isSponsored}
                isDark={isDark}
                hideBar={isDark || hideBar}
                header={header}
                subHeader={subHeader}
                scrollToRef={scrollToRef}
                gridWrapperClass={gridWrapperClass}
                gridHeaderClass={gridHeaderClass}
                dataTn={dataTn ?? DEFAULT_DATA_TN}
                item={item}
            >
                <VisibilityTracker
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible) {
                            this.onContentVisible();
                        }
                    }}
                />
                <FavoritesProvider {...favoritesProviderProps}>
                    {/* favorites  - passed to PdpRespFavoritingItem in SharedRespRelatedItem */}
                    {({ props: favorites }: $TSFixMe) => (
                        <QuickViewProvider elementRef={this.gridContainerRef}>
                            <div className="rowFlex" ref={this.gridContainerRef}>
                                {gridEntries.map(({ item: gridItem, sponsoredMetadata }, index) => {
                                    const isSponsoredItem = !!sponsoredMetadata || !!isSponsored;
                                    return (
                                        <div
                                            key={
                                                isSponsoredItem
                                                    ? `${gridItem.serviceId}_SPONSORED`
                                                    : gridItem.serviceId
                                            }
                                            className={classnames(
                                                'colLg3 colMd6 colXs6',
                                                gridItemWrapperClass
                                            )}
                                            ref={gridItemWrapperRef as LegacyRef<HTMLDivElement>}
                                        >
                                            <SharedRespRelatedItem
                                                item={gridItem}
                                                index={index}
                                                onClick={() => this.onClickItem(index)}
                                                currency={currency}
                                                viewer={viewer}
                                                favorites={favorites}
                                                user={user}
                                                onContentVisible={() => {
                                                    this.onVisibleItem(index);
                                                    if (index % 5 === 0) {
                                                        onRowImpression?.({ index });
                                                    }
                                                }}
                                                imageBackgroundColor="moonStone"
                                                isSponsored={isSponsoredItem}
                                            />
                                            {onLastItemVisibilityChange &&
                                                index === gridEntries.length - 1 && (
                                                    <VisibilityTracker
                                                        watchAfterFirstVisible
                                                        observerOptions={{
                                                            rootMargin: '40%',
                                                            threshold: 0.4,
                                                        }}
                                                        onVisibilityChange={
                                                            onLastItemVisibilityChange
                                                        }
                                                    />
                                                )}
                                        </div>
                                    );
                                })}
                            </div>
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
                {placeholder}
            </PdpRespSimilarItemsContainer>
        );
    }
}

export const PdpRespSimilarItemsGrid = createFragmentContainer(PdpRespSimilarItemsGridComponent, {
    viewer: graphql`
        fragment PdpRespSimilarItemsGrid_viewer on Viewer {
            ...SharedRespRelatedItem_viewer
        }
    `,
    similarItems: graphql`
        fragment PdpRespSimilarItemsGrid_similarItems on Item @relay(plural: true) {
            serviceId
            title
            browseUrl
            categoryCode
            ecommerceTrackingParams
            ...FavoritesProvider_item
            ...SharedRespRelatedItem_item @arguments(priceBookName: $priceBookName)
        }
    `,
    user: graphql`
        fragment PdpRespSimilarItemsGrid_user on User {
            serviceId
            ...SharedRespRelatedItem_user
        }
    `,
    item: graphql`
        fragment PdpRespSimilarItemsGrid_item on Item {
            ...PdpRespSimilarItemsContainer_item
        }
    `,
});
